import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as API from "./api/lead-management-api";
import {
  CreateLeadRequest,
  CreateNoteRequest,
  DeleteLeadRequest,
  DeleteNoteRequest,
  DownloadLeadRequest,
  DownloadSelectedLeadRequest,
  EditNoteRequest,
  GetLeadByTypeRequest,
  GetLeadsRequest,
  GetSingleLeadRequest,
  LeadResponse,
  SpecificLead,
  UpdateLeadRequest,
} from "./types";
import { LEAD_TYPE_PATHS } from "../../pages/constants";
import { LEAD_TYPE } from "../../pages/leads-management/models/ILead";

export interface ILeadManagementState {
  status: "idle" | "loading" | "failed";
  singleLead: any;
  isLastPage: boolean;
  clientsDetails: any;
  hotLeads: LeadResponse | undefined;
  allLeads: LeadResponse | undefined;
  warmLeads: LeadResponse | undefined;
  interestedLeads: LeadResponse | undefined;
  selectedLead: SpecificLead | undefined;
  leadCount: any;
  socialItems: any;
  leadType: LEAD_TYPE;
}

const initialState: ILeadManagementState = {
  allLeads: undefined,
  warmLeads: undefined,
  interestedLeads: undefined,
  selectedLead: undefined,
  hotLeads: undefined,
  status: "idle",
  singleLead: {},
  isLastPage: false,
  clientsDetails: [],
  leadCount: [],
  socialItems: [],
  leadType: LEAD_TYPE.BUSINESS,
};

export const getAllLeads = createAsyncThunk(
  "leadsManagement/getAllLeads",
  async (params: GetLeadsRequest) => {
    try {
      return await API.getAllLeadDetails(params);
    } catch (error) {
      throw error;
    }
  }
);

export const getHotLeads = createAsyncThunk(
  "leadsManagement/getHotLeads",
  async (params: GetLeadByTypeRequest) => {
    try {
      return await API.getLeadDetails(params);
    } catch (error) {
      throw error;
    }
  }
);

export const getWarmLeads = createAsyncThunk(
  "leadsManagement/getWarmLeads",
  async (params: GetLeadByTypeRequest) => {
    try {
      return await API.getLeadDetails(params);
    } catch (error) {
      throw error;
    }
  }
);

export const getInterestedLeads = createAsyncThunk(
  "leadsManagement/getInterestedLeads",
  async (params: GetLeadByTypeRequest) => {
    try {
      return await API.getLeadDetails(params);
    } catch (error) {
      throw error;
    }
  }
);

export const createLead = createAsyncThunk(
  "leadsManagement/createLead",
  async ({ lead, accessToken, url }: CreateLeadRequest) => {
    try {
      return await API.CreateLead(lead, accessToken, url);
    } catch (error) {
      throw error;
    }
  }
);

export const createNote = createAsyncThunk(
  "leadsManagement/createNote",
  async ({ note, accessToken, url }: CreateNoteRequest) => {
    try {
      return await API.CreateNote(note, accessToken, url);
    } catch (error) {
      throw error;
    }
  }
);

export const editNote = createAsyncThunk(
  "leadsManagement/editNote",
  async ({ note, accessToken, url }: EditNoteRequest) => {
    try {
      return await API.EditNote(note, accessToken, url);
    } catch (error) {
      throw error;
    }
  }
);

export const deleteLead = createAsyncThunk(
  "leadsManagement/deleteLead",
  async ({ id, accessToken, url }: DeleteLeadRequest) => {
    try {
      return await API.DeleteLead(id, accessToken, url);
    } catch (error) {
      throw error;
    }
  }
);

export const deleteNote = createAsyncThunk(
  "leadsManagement/deleteNote",
  async ({ id, accessToken, url }: DeleteNoteRequest) => {
    try {
      return await API.DeleteNote(id, accessToken, url);
    } catch (error) {
      throw error;
    }
  }
);

export const patchLeadAction = createAsyncThunk(
  "leadsManagement/patchLead",
  async (params: UpdateLeadRequest) => {
    try {
      return await API.updateLeadAction(params);
    } catch (error) {
      throw error;
    }
  }
);

export const getSingleLead = createAsyncThunk(
  "leadsManagement/getSingleLead",
  async (params: GetSingleLeadRequest) => {
    try {
      return await API.getSingleLead(params);
    } catch (error) {
      throw error;
    }
  }
);

export const downloadLead = createAsyncThunk(
  "leadsManagement/downloadLead",
  async ({ ParameterOne, url, accessToken }: DownloadLeadRequest) => {
    try {
      return await API.downloadLeads(ParameterOne, url, accessToken);
    } catch (error) {
      throw error;
    }
  }
);

export const downloadSelectedLead = createAsyncThunk(
  "leadsManagement/downloadSelectedLead",
  async ({ LeadIds, url, accessToken }: DownloadSelectedLeadRequest) => {
    try {
      return await API.downloadSelectedLeads(LeadIds, url, accessToken);
    } catch (error) {
      throw error;
    }
  }
);

export const getLeadCount = createAsyncThunk(
  "leadsManagement/getLeadCount",
  async (params: {
    url: string;
    accessToken: string;
    type: string | number;
  }) => {
    try {
      return await API.getLeadCount(params);
    } catch (error) {
      throw error;
    }
  }
);

const LeadsManagementSlice = createSlice({
  name: "leadsManagement",
  initialState,
  reducers: {
    selectedLead: (state, action: PayloadAction<SpecificLead | undefined>) => {
      state.selectedLead = action.payload;
    },
    selectLeadType: (state, action: PayloadAction<LEAD_TYPE>) => {
      state.leadType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllLeads.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        getAllLeads.fulfilled,
        (state, { payload }: { payload: LeadResponse }) => {
          state.status = "idle";
          state.allLeads = payload;
        }
      )
      .addCase(getAllLeads.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(getHotLeads.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        getHotLeads.fulfilled,
        (state, { payload }: { payload: LeadResponse }) => {
          state.status = "idle";
          state.hotLeads = payload;
        }
      )
      .addCase(getHotLeads.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(getWarmLeads.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        getWarmLeads.fulfilled,
        (state, { payload }: { payload: LeadResponse }) => {
          state.status = "idle";
          state.warmLeads = payload;
        }
      )
      .addCase(getWarmLeads.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(getInterestedLeads.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getInterestedLeads.fulfilled, (state, { payload }) => {
        state.status = "idle";
        state.interestedLeads = payload;
      })
      .addCase(getInterestedLeads.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(getSingleLead.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSingleLead.fulfilled, (state, { payload }) => {
        state.status = "idle";
        state.selectedLead = payload;
      })
      .addCase(getSingleLead.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteLead.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteLead.fulfilled, (state, { payload }) => {
        state.status = "idle";
      })
      .addCase(deleteLead.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(downloadLead.pending, (state) => {
        state.status = "loading";
      })
      .addCase(downloadLead.fulfilled, (state, { payload }) => {
        state.status = "idle";
      })
      .addCase(downloadLead.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(patchLeadAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(patchLeadAction.fulfilled, (state, { payload }) => {
        state.status = "idle";
      })
      .addCase(patchLeadAction.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getLeadCount.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getLeadCount.fulfilled, (state, { payload }) => {
        state.status = "idle";
        state.leadCount = payload;
      })
      .addCase(getLeadCount.rejected, (state) => {
        state.status = "failed";
      });
  },
});
export const { selectedLead, selectLeadType } = LeadsManagementSlice.actions;
export default LeadsManagementSlice.reducer;
